import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import wheelImage from '../../assets/spin-wheel/Spin the Wheel.png';
import { getRemainingOpenDays, isSpinWheelEligible } from './spinWheelProgress';
import { getOrCreateSessionId, loadProgressRecord, spinWheel } from './spinWheelProgressService';
import { loadSpinWheelItems } from './spinWheelItemsService';
import { FULL_BASKET_IMAGE, getFruitNames, getItemDisplayName } from './spinWheelItems';
import SpinWheelGraphic from './SpinWheelGraphic';
import { SPIN_WHEEL_COMING_SOON } from '../../config/featureFlags';
import './spinWheel.css';

const SPIN_DURATION_MS = 3000;
const FULL_BASKET_REWARD_DELAY_MS = 2200;
const FULL_SPINS = 5;
const SPIN_EASING = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
const SPIN_TRANSITION = `transform ${SPIN_DURATION_MS}ms ${SPIN_EASING}`;
// Error state holds a translation key; it is rendered through `t()` so the
// message follows the user's language.
const SPIN_PROGRESS_UNAVAILABLE_MESSAGE = 'staticPages.spinWheel.errors.progressUnavailable';
const SPIN_ITEMS_UNAVAILABLE_MESSAGE = 'staticPages.spinWheel.errors.itemsUnavailable';
const SPIN_ITEMS_EMPTY_MESSAGE = 'staticPages.spinWheel.errors.itemsEmpty';
const SPIN_SAVE_FAILED_MESSAGE = 'staticPages.spinWheel.errors.saveFailed';

const normalizeAngle = (angle) => ((angle % 360) + 360) % 360;

const getSectionAngle = (itemCount) => 360 / itemCount;

const getValidCollectedFruits = (fruits, fruitNames) => {
  if (!Array.isArray(fruits)) {
    return [];
  }

  return [...new Set(fruits)].filter((fruitName) => fruitNames.includes(fruitName));
};

const getRotationForSectionAtTop = (sectionIndex, itemCount) => {
  const sectionAngle = getSectionAngle(itemCount);
  const sectionCenterAngle = sectionIndex * sectionAngle + sectionAngle / 2;

  return normalizeAngle(360 - sectionCenterAngle);
};

const getNextClockwiseRotation = (currentRotation, targetSectionIndex, itemCount) => {
  const currentAngle = normalizeAngle(currentRotation);
  const targetAngle = getRotationForSectionAtTop(targetSectionIndex, itemCount);
  const deltaToTarget = normalizeAngle(targetAngle - currentAngle);

  return currentRotation + FULL_SPINS * 360 + deltaToTarget;
};

// Stock on the server only moves for a prize that was really handed over.
const stockMayHaveChanged = (wonItem, result) =>
  (wonItem.type !== 'try-again' && !result.isDuplicate && wonItem.quantity !== null) ||
  Boolean(result.reward && result.reward.quantity !== null);

const buildEligibilityMessage = (t, progress, isLoading, errorKey) => {
  if (isLoading) {
    return t('staticPages.spinWheel.status.checking');
  }

  if (errorKey) {
    return t(errorKey);
  }

  if (isSpinWheelEligible(progress)) {
    const spinCount = progress?.spins_available || 1;

    return t(
      spinCount === 1
        ? 'staticPages.spinWheel.status.spinAvailableOne'
        : 'staticPages.spinWheel.status.spinAvailableOther',
      { count: spinCount }
    );
  }

  const remainingVisits = getRemainingOpenDays(progress);

  return t(
    remainingVisits === 1 ? 'staticPages.spinWheel.status.visitMoreOne' : 'staticPages.spinWheel.status.visitMoreOther',
    { count: remainingVisits }
  );
};

const getFruitResultSubtitle = (t, resultDetails) => {
  if (resultDetails?.isDuplicate) {
    return t('staticPages.spinWheel.result.duplicateFruit');
  }

  if (resultDetails?.collectionComplete) {
    return t('staticPages.spinWheel.result.allFruits');
  }

  return t('staticPages.spinWheel.result.newFruit');
};

const getFruitImagesByName = (items) =>
  items.reduce(
    (imagesByName, item) => (item.type === 'fruit' ? { ...imagesByName, [item.name]: item.image } : imagesByName),
    {}
  );

function FruitCollectionResult({ collectedFruits, fruitNames, items, onClose, resultDetails, selectedResult }) {
  const { t } = useTranslation();
  const selectedResultImage =
    resultDetails?.collectionComplete && !resultDetails?.isDuplicate
      ? FULL_BASKET_IMAGE
      : (selectedResult.resultImage ?? selectedResult.image);
  const fruitImagesByName = getFruitImagesByName(items);
  const fruitCount = collectedFruits.length;
  const fruitCountLabel = t(
    fruitCount === 1 ? 'staticPages.spinWheel.result.fruitCountOne' : 'staticPages.spinWheel.result.fruitCountOther',
    { count: fruitCount }
  );
  const itemCollectedLabel = (name) =>
    t('staticPages.spinWheel.result.itemCollected', { name: getItemDisplayName(t, name) });
  const collectionSlots = fruitNames.map((slotName, index) => ({
    slotName,
    fruitName: collectedFruits[index] || null,
  }));

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (resultDetails?.showRewardScreen) {
    return (
      <div className="spin-wheel__modal-overlay" onClick={handleOverlayClick}>
        <div
          role="dialog"
          aria-modal="true"
          className="spin-wheel__reward-result"
          aria-label={itemCollectedLabel(resultDetails.rewardName)}
        >
          <button type="button" className="spin-wheel__close-btn" onClick={onClose}>
            x
          </button>
          <img
            src={resultDetails.rewardImage}
            alt={itemCollectedLabel(resultDetails.rewardName)}
            className="spin-wheel__reward-screen-image"
          />
        </div>
      </div>
    );
  }

  const title = resultDetails?.isDuplicate
    ? t('staticPages.spinWheel.result.alreadyCollected')
    : t('staticPages.spinWheel.result.congratulations');
  const subtitle = getFruitResultSubtitle(t, resultDetails);

  return (
    <div className="spin-wheel__modal-overlay" onClick={handleOverlayClick}>
      <div
        role="dialog"
        aria-modal="true"
        className="spin-wheel__fruit-result"
        aria-label={t('staticPages.spinWheel.result.dialogLabel')}
      >
        <button type="button" className="spin-wheel__close-btn spin-wheel__fruit-close-btn" onClick={onClose}>
          x
        </button>
        <div className="spin-wheel__fruit-hero">
          <h2>{title}</h2>
          <p>{subtitle}</p>
          <img
            src={selectedResultImage}
            alt={itemCollectedLabel(selectedResult.name)}
            className="spin-wheel__fruit-gif"
          />
          {resultDetails?.isDuplicate && (
            <p className="spin-wheel__fruit-note">{t('staticPages.spinWheel.result.spinAgainNextTime')}</p>
          )}
        </div>
        <div
          className="spin-wheel__fruit-slots"
          aria-label={fruitCountLabel}
          style={{ '--spin-wheel-slot-count': collectionSlots.length }}
        >
          {collectionSlots.map(({ slotName, fruitName }) => (
            <div
              key={slotName}
              className="spin-wheel__fruit-slot"
              aria-label={fruitName ? itemCollectedLabel(fruitName) : t('staticPages.spinWheel.result.emptySlot')}
            >
              {fruitName && (
                <img
                  src={fruitImagesByName[fruitName]}
                  alt={getItemDisplayName(t, fruitName)}
                  className="spin-wheel__fruit-slot-image"
                />
              )}
            </div>
          ))}
        </div>
        <p className="spin-wheel__fruit-count">{fruitCountLabel}</p>
      </div>
    </div>
  );
}

function SpinWheelComingSoon() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="spin-wheel-page">
      <div
        className="spin-wheel__coming-soon-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spin-wheel-coming-soon-title"
      >
        <div className="spin-wheel__coming-soon-modal">
          <img src={wheelImage} alt="" className="spin-wheel__coming-soon-image" />
          <h2 id="spin-wheel-coming-soon-title">{t('staticPages.spinWheelComingSoon.title')}</h2>
          <p>{t('staticPages.spinWheelComingSoon.description')}</p>
          <button type="button" className="spin-wheel__coming-soon-button" onClick={() => navigate('/homepage')}>
            {t('staticPages.spinWheelComingSoon.backToHome')}
          </button>
        </div>
      </div>
    </div>
  );
}

function SpinWheelFeature() {
  const { t } = useTranslation();
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [resultDetails, setResultDetails] = useState(null);
  const [spinProgress, setSpinProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const rotationRef = useRef(0);
  const timeoutRef = useRef(null);
  const rewardTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const itemsNeedReloadRef = useRef(false);

  const clearRewardTimeout = () => {
    if (rewardTimeoutRef.current) {
      clearTimeout(rewardTimeoutRef.current);
      rewardTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    isMountedRef.current = true;

    const initializeSpinWheel = async () => {
      setLoading(true);
      setLoadError('');

      const [progressResult, itemsResult] = await Promise.allSettled([
        loadProgressRecord(getOrCreateSessionId()),
        loadSpinWheelItems(),
      ]);

      if (!isMountedRef.current) {
        return;
      }

      if (progressResult.status === 'fulfilled') {
        setSpinProgress(progressResult.value);
      } else {
        setSpinProgress(null);
        setLoadError(SPIN_PROGRESS_UNAVAILABLE_MESSAGE);

        // eslint-disable-next-line no-console
        console.error('Error loading spin wheel progress:', progressResult.reason);
      }

      if (itemsResult.status === 'fulfilled') {
        setItems(itemsResult.value);
      } else {
        setItems([]);
        setLoadError(SPIN_ITEMS_UNAVAILABLE_MESSAGE);

        // eslint-disable-next-line no-console
        console.error('Error loading spin wheel items:', itemsResult.reason);
      }

      setLoading(false);
    };

    initializeSpinWheel();

    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      clearRewardTimeout();
    };
  }, []);

  const fruitNames = getFruitNames(items);
  const collectedFruits = getValidCollectedFruits(spinProgress?.collected_fruits, fruitNames);
  const spinEligible = isSpinWheelEligible(spinProgress);
  const statusMessage = buildEligibilityMessage(
    t,
    spinProgress,
    loading,
    loadError || (items.length === 0 && !loading ? SPIN_ITEMS_EMPTY_MESSAGE : '')
  );
  const spinButtonDisabled = spinning || loading || !spinEligible || Boolean(loadError) || items.length === 0;

  // Picks up the new stock levels once the result is out of the way, so an item
  // that just ran out is gone from the next spin.
  const reloadItemsIfNeeded = async () => {
    if (!itemsNeedReloadRef.current) {
      return;
    }

    itemsNeedReloadRef.current = false;

    try {
      const reloadedItems = await loadSpinWheelItems();

      if (isMountedRef.current) {
        setItems(reloadedItems);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error reloading spin wheel items:', error);
    }
  };

  // Shows the prize the server picked. The server has already updated stock
  // and the basket, so this only presents that outcome.
  const finishSpin = (wonItem, result, nextProgress) => {
    let nextResultDetails = null;

    if (wonItem.type === 'fruit') {
      nextResultDetails = {
        collectionComplete: result.collectionComplete,
        isDuplicate: result.isDuplicate,
        showRewardScreen: false,
      };

      // Completing the basket hands over the reward, so the reward screen
      // takes over from the full basket after a short pause.
      if (result.collectionComplete && !result.isDuplicate && result.reward) {
        const { reward } = result;

        rewardTimeoutRef.current = setTimeout(() => {
          setResultDetails((currentDetails) =>
            currentDetails?.collectionComplete && !currentDetails?.isDuplicate
              ? {
                  ...currentDetails,
                  showRewardScreen: true,
                  rewardName: reward.name,
                  rewardImage: reward.resultImage ?? reward.image,
                }
              : currentDetails
          );
          rewardTimeoutRef.current = null;
        }, FULL_BASKET_REWARD_DELAY_MS);
      }
    }

    // The wheel itself is not redrawn yet, so the slices do not shift while
    // the result is on screen.
    if (stockMayHaveChanged(wonItem, result)) {
      itemsNeedReloadRef.current = true;
    }

    setSpinProgress(nextProgress);
    setSpinning(false);
    setResultDetails(nextResultDetails);
    setSelectedItem(wonItem);
  };

  // The server said this session has no spin. Whatever the page believed is
  // stale, so refresh it from the server.
  const refreshProgressAfterRefusal = async () => {
    try {
      const refreshedProgress = await loadProgressRecord(getOrCreateSessionId());

      if (isMountedRef.current) {
        setSpinProgress(refreshedProgress);
      }
    } catch (error) {
      if (isMountedRef.current) {
        setSpinProgress((currentProgress) => ({ ...currentProgress, spins_available: 0 }));
      }

      // eslint-disable-next-line no-console
      console.error('Error refreshing spin wheel progress:', error);
    }
  };

  const handleSpin = async () => {
    if (spinning || loading || loadError || items.length === 0) return;

    if (!isSpinWheelEligible(spinProgress)) {
      return;
    }

    setSpinning(true);
    setSelectedItem(null);
    setResultDetails(null);
    setLoadError('');
    clearRewardTimeout();

    let spinOutcome;

    try {
      spinOutcome = await spinWheel(getOrCreateSessionId());
    } catch (error) {
      setSpinning(false);
      setLoadError(SPIN_SAVE_FAILED_MESSAGE);

      // eslint-disable-next-line no-console
      console.error('Error spinning the wheel:', error);
      return;
    }

    if (!isMountedRef.current) {
      return;
    }

    if (!spinOutcome) {
      await refreshProgressAfterRefusal();
      setSpinning(false);
      return;
    }

    const { item: wonItem, result, progress: nextProgress } = spinOutcome;

    // The spin is spent now; the basket only fills once the wheel has stopped.
    setSpinProgress({ ...nextProgress, collected_fruits: spinProgress?.collected_fruits ?? [] });

    let spinItems = items;
    let targetSectionIndex = spinItems.findIndex((item) => item.id === wonItem.id);

    // The wheel changed since it was loaded (an item was added or ran out).
    // Redraw it so the prize has a slice to land on.
    if (targetSectionIndex === -1) {
      try {
        spinItems = await loadSpinWheelItems();
        targetSectionIndex = spinItems.findIndex((item) => item.id === wonItem.id);

        if (isMountedRef.current) {
          setItems(spinItems);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error reloading spin wheel items:', error);
      }
    }

    if (!isMountedRef.current) {
      return;
    }

    if (targetSectionIndex === -1) {
      finishSpin(wonItem, result, nextProgress);
      return;
    }

    const finalRotation = getNextClockwiseRotation(rotationRef.current, targetSectionIndex, spinItems.length);

    rotationRef.current = finalRotation;
    setRotation(finalRotation);

    timeoutRef.current = setTimeout(() => {
      finishSpin(wonItem, result, nextProgress);
      timeoutRef.current = null;
    }, SPIN_DURATION_MS);
  };

  const handleCloseModal = () => {
    clearRewardTimeout();
    setSelectedItem(null);
    setResultDetails(null);
    reloadItemsIfNeeded();
  };

  const handleTryAgain = () => {
    setSelectedItem(null);
    handleSpin();
  };

  const handleCollectReward = () => {
    setSelectedItem(null);
    reloadItemsIfNeeded();
  };

  const selectedResultImage = selectedItem?.resultImage ?? selectedItem?.image;
  const resultImageClassName = selectedItem?.resultImage
    ? 'spin-wheel__result-image spin-wheel__result-image--screen'
    : 'spin-wheel__result-image';
  const selectedItemLabel = selectedItem ? getItemDisplayName(t, selectedItem.name) : '';
  // Slice labels follow the user's language; the stored name stays the key.
  const wheelItems = items.map((item) => ({ ...item, label: getItemDisplayName(t, item.name) }));

  return (
    <div className="spin-wheel-page">
      <h1>{t('staticPages.spinWheel.title')}</h1>
      <div className="spin-wheel__arrow-container">
        <div className="spin-wheel__arrow" />
      </div>
      <div className="spin-wheel__preview">
        {items.length > 0 ? (
          <SpinWheelGraphic
            items={wheelItems}
            rotation={rotation}
            spinning={spinning}
            transition={SPIN_TRANSITION}
            label={t('staticPages.spinWheel.wheelAlt')}
          />
        ) : (
          <img
            src={wheelImage}
            alt={t('staticPages.spinWheel.wheelAlt')}
            className="spin-wheel__image spin-wheel__image--placeholder"
          />
        )}
      </div>
      <button type="button" className="spin-wheel__button" onClick={handleSpin} disabled={spinButtonDisabled}>
        {spinning ? t('staticPages.spinWheel.spinningButton') : t('staticPages.spinWheel.spinButton')}
      </button>
      <p className="spin-wheel__eligibility-message">{statusMessage}</p>

      {selectedItem?.type === 'fruit' && (
        <FruitCollectionResult
          collectedFruits={collectedFruits}
          fruitNames={fruitNames}
          items={items}
          onClose={handleCloseModal}
          resultDetails={resultDetails}
          selectedResult={selectedItem}
        />
      )}

      {selectedItem && selectedItem.type !== 'fruit' && (
        <div className="spin-wheel__modal-overlay" onClick={handleCloseModal}>
          <div
            className={`spin-wheel__modal spin-wheel__modal--${selectedItem.type}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="spin-wheel__close-btn" onClick={handleCloseModal}>
              x
            </button>
            <div className="spin-wheel__modal-content">
              <h2>{selectedItemLabel}</h2>
              <img src={selectedResultImage} alt={selectedItemLabel} className={resultImageClassName} />
              {selectedItem.type === 'reward' && (
                <button type="button" className="spin-wheel__result-action" onClick={handleCollectReward}>
                  {t('staticPages.spinWheel.result.collectReward')}
                </button>
              )}
              {selectedItem.type === 'try-again' && (
                <>
                  {!spinEligible && <p className="spin-wheel__result-message">{statusMessage}</p>}
                  <button
                    type="button"
                    className="spin-wheel__result-action"
                    onClick={handleTryAgain}
                    disabled={!spinEligible || spinning}
                  >
                    {spinEligible
                      ? t('staticPages.spinWheel.result.tryAgain')
                      : t('staticPages.spinWheel.result.comeBackLater')}
                  </button>
                </>
              )}
              {selectedItem.type !== 'try-again' && !spinEligible && (
                <p className="spin-wheel__result-message">{statusMessage}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SpinWheel() {
  if (SPIN_WHEEL_COMING_SOON) {
    return <SpinWheelComingSoon />;
  }

  return <SpinWheelFeature />;
}
